(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-reviews-carousel';
  var sectionState = new Map();

  function initAutoplay(section, advanceSlide) {
    var autoplayEnabled = section.dataset.autoplay === 'true';
    if (!autoplayEnabled) return null;

    var speedSeconds = parseInt(section.dataset.autoplaySpeed, 10) || 5;
    var intervalMs = speedSeconds * 1000;

    var rafId = null;
    var lastAdvance = performance.now();
    var userHasTakenControl = false;
    var programmaticScroll = false;
    var programmaticSettleTimer = null;
    var SCROLL_SETTLE_MS = 150;

    function disableAutoplayPermanently() {
      if (userHasTakenControl) return;
      userHasTakenControl = true;
      stop();
    }

    function markProgrammatic() {
      programmaticScroll = true;
      if (programmaticSettleTimer) clearTimeout(programmaticSettleTimer);
    }

    function settleProgrammatic() {
      if (programmaticSettleTimer) clearTimeout(programmaticSettleTimer);
      programmaticSettleTimer = setTimeout(function () {
        programmaticScroll = false;
        programmaticSettleTimer = null;
      }, SCROLL_SETTLE_MS);
    }

    function tick(now) {
      if (userHasTakenControl) return;
      if (now - lastAdvance >= intervalMs) {
        markProgrammatic();
        advanceSlide(section);
        lastAdvance = now;
      }
      rafId = requestAnimationFrame(tick);
    }

    function start() {
      if (userHasTakenControl) return;
      if (rafId) return;
      lastAdvance = performance.now();
      rafId = requestAnimationFrame(tick);
    }

    function stop() {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) {
      return {
        start: function () {},
        stop: function () {},
        destroy: function () {},
        disableAutoplayPermanently: function () {},
        markProgrammatic: function () {}
      };
    }

    document.addEventListener('visibilitychange', function () {
      if (userHasTakenControl) return;
      document.hidden ? stop() : start();
    });

    var track = section.querySelector('.oddit-section-reviews-carousel__track');
    if (track) {
      track.addEventListener('mouseenter', function () {
        if (!userHasTakenControl) stop();
      });
      track.addEventListener('mouseleave', function () {
        if (!userHasTakenControl) start();
      });
      track.addEventListener('focusin', disableAutoplayPermanently);
      track.addEventListener('touchstart', disableAutoplayPermanently, { passive: true });
      track.addEventListener('scroll', function () {
        if (userHasTakenControl) return;
        if (programmaticScroll) {
          settleProgrammatic();
          return;
        }
        disableAutoplayPermanently();
      }, { passive: true });
    }

    reducedMotion.addEventListener('change', function (e) {
      if (userHasTakenControl) return;
      e.matches ? stop() : start();
    });

    start();

    return {
      start: start,
      stop: stop,
      destroy: function () {
        stop();
        if (programmaticSettleTimer) {
          clearTimeout(programmaticSettleTimer);
          programmaticSettleTimer = null;
        }
      },
      disableAutoplayPermanently: disableAutoplayPermanently,
      markProgrammatic: markProgrammatic
    };
  }

  var _scrollAnimMap = new WeakMap();

  // Symmetric ease for taps/autoplay where there's no prior motion.
  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // Decelerating ease for drag/swipe release — the motion continues from the
  // user's gesture and settles, instead of hitching with a slow start.
  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function smoothScrollTo(element, target, duration, easing, onDone) {
    var ease = easing || easeInOutCubic;
    var existing = _scrollAnimMap.get(element);
    if (existing) cancelAnimationFrame(existing);

    var start = element.scrollLeft;
    var change = target - start;

    element.style.scrollSnapType = 'none';

    if (change === 0) {
      element.style.scrollSnapType = '';
      if (onDone) onDone();
      return;
    }

    var startTime = null;

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var elapsed = timestamp - startTime;
      var t = Math.min(elapsed / duration, 1);
      element.scrollLeft = start + change * ease(t);
      if (t < 1) {
        _scrollAnimMap.set(element, requestAnimationFrame(step));
      } else {
        element.scrollLeft = target;
        element.style.scrollSnapType = '';
        _scrollAnimMap.delete(element);
        if (onDone) onDone();
      }
    }

    _scrollAnimMap.set(element, requestAnimationFrame(step));
  }

  function getSlides(section) {
    return section.querySelectorAll('.oddit-section-reviews-carousel__slide');
  }

  function getDots(section) {
    return section.querySelectorAll('.oddit-section-reviews-carousel__dot');
  }

  function getCurrentIndex(section) {
    var track = section.querySelector('.oddit-section-reviews-carousel__track');
    if (!track) return 0;
    var slides = getSlides(section);
    if (!slides.length) return 0;
    var slideWidth = slides[0].getBoundingClientRect().width;
    if (!slideWidth) return 0;
    return Math.round(track.scrollLeft / slideWidth);
  }

  function updateActiveStates(section, index) {
    var slides = getSlides(section);
    var dots = getDots(section);

    slides.forEach(function (slide, i) {
      if (i === index) {
        slide.classList.add('oddit-section-reviews-carousel__slide--active');
        slide.setAttribute('aria-hidden', 'false');
      } else {
        slide.classList.remove('oddit-section-reviews-carousel__slide--active');
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    dots.forEach(function (dot, i) {
      if (i === index) {
        dot.classList.add('oddit-section-reviews-carousel__dot--active');
        dot.setAttribute('aria-selected', 'true');
      } else {
        dot.classList.remove('oddit-section-reviews-carousel__dot--active');
        dot.setAttribute('aria-selected', 'false');
      }
    });
  }

  function goToSlide(section, index, opts) {
    var track = section.querySelector('.oddit-section-reviews-carousel__track');
    var slides = getSlides(section);
    if (!track || !slides.length) return;

    var total = slides.length;
    if (index < 0) index = total - 1;
    if (index >= total) index = 0;

    opts = opts || {};
    var duration = opts.duration || 420;
    var easing = opts.easing || easeInOutCubic;

    var slideWidth = slides[0].getBoundingClientRect().width;
    smoothScrollTo(track, slideWidth * index, duration, easing, opts.onDone);

    updateActiveStates(section, index);
  }

  function advanceSlide(section) {
    var current = getCurrentIndex(section);
    var slides = getSlides(section);
    var next = (current + 1) % slides.length;
    goToSlide(section, next);
  }

  function initSection(section) {
    if (sectionState.has(section)) return;

    var dots = getDots(section);
    var track = section.querySelector('.oddit-section-reviews-carousel__track');

    var state = {
      autoplay: null,
      handleScroll: null,
      handleDotClick: null
    };

    state.autoplay = initAutoplay(section, advanceSlide);

    state.handleDotClick = function (e) {
      var dot = e.target.closest('.oddit-section-reviews-carousel__dot');
      if (!dot) return;
      var index = parseInt(dot.getAttribute('data-slide-target'), 10);
      if (isNaN(index)) return;
      if (state.autoplay) {
        state.autoplay.markProgrammatic();
      }
      goToSlide(section, index);
      if (state.autoplay) {
        state.autoplay.disableAutoplayPermanently();
      }
    };

    dots.forEach(function (dot) {
      dot.addEventListener('click', state.handleDotClick);
    });

    if (track) {
      var scrollDebounce = null;
      state.handleScroll = function () {
        if (scrollDebounce) cancelAnimationFrame(scrollDebounce);
        scrollDebounce = requestAnimationFrame(function () {
          var index = getCurrentIndex(section);
          updateActiveStates(section, index);
        });
      };
      track.addEventListener('scroll', state.handleScroll, { passive: true });
      state._scrollDebounce = function () {
        if (scrollDebounce) cancelAnimationFrame(scrollDebounce);
      };

      // Mouse/pen drag — touch is handled natively via scroll-snap + touchend below.
      var drag = {
        active: false,
        pointerId: null,
        startX: 0,
        startScrollLeft: 0,
        moved: false
      };
      var DRAG_MOVE_THRESHOLD = 4;

      state.handlePointerDown = function (e) {
        if (e.pointerType === 'touch') return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        var animId = _scrollAnimMap.get(track);
        if (animId) { cancelAnimationFrame(animId); _scrollAnimMap.delete(track); track.style.scrollSnapType = ''; }
        drag.active = true;
        drag.pointerId = e.pointerId;
        drag.startX = e.clientX;
        drag.startScrollLeft = track.scrollLeft;
        drag.moved = false;
      };

      state.handlePointerMove = function (e) {
        if (!drag.active || e.pointerId !== drag.pointerId) return;
        var dx = e.clientX - drag.startX;
        if (!drag.moved) {
          if (Math.abs(dx) < DRAG_MOVE_THRESHOLD) return;
          drag.moved = true;
          track.classList.add('oddit-section-reviews-carousel__track--dragging');
          try { track.setPointerCapture(drag.pointerId); } catch (err) {}
          if (state.autoplay) state.autoplay.disableAutoplayPermanently();
        }
        track.scrollLeft = drag.startScrollLeft - dx;
        if (e.cancelable) e.preventDefault();
      };

      state.endDrag = function (e) {
        if (!drag.active || (e && e.pointerId !== drag.pointerId)) return;
        var wasMoved = drag.moved;
        var startScrollLeft = drag.startScrollLeft;
        drag.active = false;
        drag.pointerId = null;
        try { track.releasePointerCapture(e.pointerId); } catch (err) {}
        if (!wasMoved) return;

        // Pin scroll-snap off (inline) before dropping the --dragging class, so
        // the base `scroll-snap-type: x mandatory` can't fire an instant native
        // snap from the mid-drag position and fight the smooth animation.
        // smoothScrollTo restores it once settled on the exact slide.
        track.style.scrollSnapType = 'none';
        track.classList.remove('oddit-section-reviews-carousel__track--dragging');
        // Advance based on drag direction with a low threshold, so a light swipe
        // moves to the next slide rather than snapping back.
        var slides = getSlides(section);
        if (slides.length) {
          var slideWidth = slides[0].getBoundingClientRect().width || 1;
          var startIndex = Math.round(startScrollLeft / slideWidth);
          var delta = track.scrollLeft - startScrollLeft;
          var advanceThreshold = Math.min(slideWidth * 0.08, 20);
          var targetIndex = startIndex;
          if (delta > advanceThreshold) targetIndex = startIndex + 1;
          else if (delta < -advanceThreshold) targetIndex = startIndex - 1;
          if (targetIndex < 0) targetIndex = 0;
          if (targetIndex > slides.length - 1) targetIndex = slides.length - 1;
          goToSlide(section, targetIndex, { duration: 380, easing: easeOutCubic });
        }
      };

      state.handleDragClick = function (e) {
        if (drag.moved) {
          e.preventDefault();
          e.stopPropagation();
          drag.moved = false;
        }
      };

      track.addEventListener('pointerdown', state.handlePointerDown);
      track.addEventListener('pointermove', state.handlePointerMove);
      track.addEventListener('pointerup', state.endDrag);
      track.addEventListener('pointercancel', state.endDrag);
      track.addEventListener('click', state.handleDragClick, true);

      // Forward predominantly-vertical wheel events to the page so the user
      // can scroll past the carousel without it hijacking vertical scrolling.
      state.handleWheel = function (e) {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
          window.scrollBy({ top: e.deltaY, behavior: 'auto' });
        }
      };
      track.addEventListener('wheel', state.handleWheel, { passive: false });

      // Touch snap — native scroll handles momentum; on touchend we snap to the
      // directionally-correct slide so any small swipe reliably advances.
      var touch = { startX: 0, startScrollLeft: 0 };

      state.handleTouchStart = function (e) {
        var animId = _scrollAnimMap.get(track);
        if (animId) { cancelAnimationFrame(animId); _scrollAnimMap.delete(track); track.style.scrollSnapType = ''; }
        touch.startX = e.touches[0].clientX;
        touch.startScrollLeft = track.scrollLeft;
        if (state.autoplay) state.autoplay.disableAutoplayPermanently();
      };

      state.handleTouchEnd = function () {
        var slides = getSlides(section);
        if (!slides.length) return;
        var slideWidth = slides[0].getBoundingClientRect().width || 1;
        var startIndex = Math.round(touch.startScrollLeft / slideWidth);
        var delta = track.scrollLeft - touch.startScrollLeft;
        var threshold = Math.min(slideWidth * 0.08, 20);
        var targetIndex = startIndex;
        if (delta > threshold) targetIndex = startIndex + 1;
        else if (delta < -threshold) targetIndex = startIndex - 1;
        if (targetIndex < 0) targetIndex = 0;
        if (targetIndex > slides.length - 1) targetIndex = slides.length - 1;
        goToSlide(section, targetIndex, { duration: 380, easing: easeOutCubic });
      };

      track.addEventListener('touchstart', state.handleTouchStart, { passive: true });
      track.addEventListener('touchend', state.handleTouchEnd, { passive: true });
    }

    sectionState.set(section, state);
  }

  function destroySection(section) {
    var state = sectionState.get(section);
    if (!state) return;

    if (state.autoplay) state.autoplay.destroy();
    if (state._scrollDebounce) state._scrollDebounce();

    var dots = getDots(section);
    dots.forEach(function (dot) {
      if (state.handleDotClick) dot.removeEventListener('click', state.handleDotClick);
    });

    var track = section.querySelector('.oddit-section-reviews-carousel__track');
    if (track) {
      if (state.handleScroll) track.removeEventListener('scroll', state.handleScroll);
      if (state.handlePointerDown) track.removeEventListener('pointerdown', state.handlePointerDown);
      if (state.handlePointerMove) track.removeEventListener('pointermove', state.handlePointerMove);
      if (state.endDrag) {
        track.removeEventListener('pointerup', state.endDrag);
        track.removeEventListener('pointercancel', state.endDrag);
      }
      if (state.handleDragClick) track.removeEventListener('click', state.handleDragClick, true);
      if (state.handleWheel) track.removeEventListener('wheel', state.handleWheel);
      if (state.handleTouchStart) track.removeEventListener('touchstart', state.handleTouchStart);
      if (state.handleTouchEnd) track.removeEventListener('touchend', state.handleTouchEnd);
    }

    sectionState.delete(section);
  }

  function initAll(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll(SECTION_SELECTOR);
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var sections = e.target.querySelectorAll(SECTION_SELECTOR);
    sections.forEach(destroySection);
  });
})();